import {Component, inject, ViewChild} from '@angular/core';
import {BaseComponentWithDrawer} from "../base-component-with-drawer.component";
import {GlobalModelService} from "../../../services/GlobalModel.service";
import {DxFormComponent} from "devextreme-angular";
import {GatewayType} from "../../../models/gateway-type/GatewayType";
import {GatewayTypeOpeService} from "../../../services/ope/gatewayTypeOpe.service";
import {GetGatewayTypesCB} from "../../../ope/gateway-type/GetGatewayTypesCB";
import {PutGatewayTypeCB} from "../../../ope/gateway-type/PutGatewayTypeCB";
import {DelGatewayTypeCB} from "../../../ope/gateway-type/DelGatewayTypeCB";
import {DxButtonTypes} from "devextreme-angular/ui/button";

@Component({
  selector: 'app-gateway-type',
  templateUrl: './gateway-type.component.html',
  styleUrls: ['./gateway-type.component.css', '../../common/drawer-style.css', '../base-component-with-drawer.component.css']
})
export class GatewayTypeComponent extends BaseComponentWithDrawer {
  gmService: GlobalModelService = inject(GlobalModelService);
  gatewayTypeOpeService: GatewayTypeOpeService = inject(GatewayTypeOpeService);

  @ViewChild('newGatewayTypeForm', {static: false}) newGatewayTypeForm!: DxFormComponent;
  @ViewChild('editGatewayTypeForm', {static: false}) editGatewayTypeForm!: DxFormComponent;

  /**
   * All gateway types
   */
  gatewayTypes: GatewayType[] = [];

  /**
   * gateway types tab for adding/editing a gateway type
   */
  gatewayTypeTabs: any[] = [
    {
      id: 0,
      text: "Informazioni"
    }
  ];

  /**
   * Selected gateway type on data grid row click
   */
  selectedGatewayType?: GatewayType;

  /**
   * Copy of selected gateway type, storing temporary changes (can roll back to selectedGatewayType if something goes wrong)
   */
  editedGatewayType?: GatewayType;

  /**
   * New gateway type used when opening new gateway type form
   */
  newGatewayType?: GatewayType = new GatewayType();

  /**
   * Enable lateral drawer form when true
   */
  isEditMode: boolean = false;

  /**
   * Tab selected gateway type form (new/edit)
   */
  selectedGatewayTypeTab: number = 0;

  /**
   * Open new gateway type dialog when true
   */
  addGatewayType: boolean = false;

  /**
   * Open delete gateway type dialog when true
   */
  deletingGatewayType: boolean = false;

  /**
   * True if the user changed anything in edit gateway type form
   */
  gatewayTypeEdited: boolean = false;

  /**
   * True if new gateway type is valid and can be saved
   */
  isNewFormValid: boolean | undefined = false;

  /**
   * True if edited gateway type is valid and can be updated
   */
  isEditFormValid: boolean | undefined = false;

  /**
   * Id of the selected gateway type in the main tree list (selected by clicking on a row)
   */
  selectedRowGatewayType: any;

  discardEdits: boolean = false;


  /**
   * Text box options for gateway type fields
   */
  gatewayTypeNewTextOptions = {
    valueChangeEvent: 'keyup',
  };

  gatewayTypeNewTextAreaOptions = {
    valueChangeEvent: 'keyup',
    autoResizeEnabled: true
  };

  gatewayTypeEditTextOptions = {
    valueChangeEvent: 'keyup',
    disabled: !this.isEditMode
  };

  gatewayTypeEditTextAreaOptions = {
    valueChangeEvent: 'keyup',
    disabled: !this.isEditMode,
    autoResizeEnabled: true
  };

  gatewayTypeEditCheckBoxOptions = {
    disabled: !this.isEditMode
  };

  rtuPortName: string = "";

  addRtuPortButton: DxButtonTypes.Properties = {
    icon: 'plus',
    stylingMode: 'text',
    width: 32,
    onClick: (e) => {
      if (this.rtuPortName.trim().length > 0) {
        if (this.addGatewayType) {
          this.newGatewayType?.additionalInfo?.rtuPorts?.push(this.rtuPortName.trim());
          this.checkNewGatewayTypeValidation();
        } else {
          this.editedGatewayType?.additionalInfo?.rtuPorts?.push(this.rtuPortName.trim());
          this.checkEditGatewayTypeValidation();
        }
        this.rtuPortName = "";
      }
    },
  };

  ngOnInit() {
    super.ngOnInit();
    this.getGatewayTypes();
  }

  /**
   * Get all gateway types
   */
  getGatewayTypes() {
    const cb = new GetGatewayTypesCB();

    this.gatewayTypeOpeService.getGatewayTypes(cb).then((_) => {

      this.gatewayTypes = cb.o.gatewayTypes;

      this.gatewayTypeEdited = false;

      // if the drawer was already open, update the infos (probably a gateway type has been updated)
      if (!!this.selectedGatewayType) {
        this.selectedGatewayType = JSON.parse(JSON.stringify(this.gatewayTypes.find(g => g.id === this.selectedGatewayType!.id)));
        this.editedGatewayType = JSON.parse(JSON.stringify(this.selectedGatewayType));
      }

    }).catch((error) => {
      if (!!this.selectedGatewayType)
        this.editedGatewayType = JSON.parse(JSON.stringify(this.selectedGatewayType));
    });
  }

  /**
   * Add or update gateway type
   */
  putGatewayType(gatewayType: GatewayType | undefined) {

    // create cb
    const cb = new PutGatewayTypeCB();
    cb.i.id = gatewayType?.id;
    cb.i.modelName = gatewayType!.modelName;
    cb.i.partNumber = gatewayType!.partNumber;
    cb.i.osType = gatewayType!.osType;
    cb.i.additionalInfo = gatewayType?.additionalInfo!;

    this.gatewayTypeOpeService.putGatewayType(cb).then((_) => {
      this.addGatewayType = false;

      this.isNewFormValid = false;

      if (!gatewayType!.id)
        this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.GATEWAY_TYPE.TEXT.CREATED"));
      else
        this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.GATEWAY_TYPE.TEXT.UPDATED"));

      // reload gateway types list
      this.getGatewayTypes();

    }).catch((error) => {

      this.getGatewayTypes();

      // roll back to previous gateway type version
      if (!!this.selectedGatewayType)
        this.editedGatewayType = JSON.parse(JSON.stringify(this.selectedGatewayType));
    });
  }

  /**
   * Open drawer when clicking on a tree list row (only if the event is not a click on the arrow to open the children)
   * @param $event
   */
  onGatewayTypeRowClick($event: any) {

    if (!this.selectedGatewayType) {
      this.selectedGatewayType = $event.data;
      this.editedGatewayType = JSON.parse(JSON.stringify(this.selectedGatewayType));
      this.isDrawerOpen = true;
    } else {
      if (this.selectedGatewayType!.id === $event.data.id) {
        this.closeDrawer();
      } else {
        this.switchGatewayType($event.data);
      }
    }

  }

  switchGatewayType(gatewayType: GatewayType) {
    if (this.gatewayTypeEdited && this.isEditMode) {
      this.showDiscardEditsDialog().show().done((dialogResult: any) => {
        if (dialogResult) {
          this.resetDrawer();
          this.selectedGatewayType = gatewayType;
          this.editedGatewayType = JSON.parse(JSON.stringify(this.selectedGatewayType));
        } else {
          this.selectedRowGatewayType = [this.selectedGatewayType!.id];
        }
      });
    } else {
      this.resetDrawer();
      this.selectedGatewayType = gatewayType;
      this.editedGatewayType = JSON.parse(JSON.stringify(this.selectedGatewayType));
    }

  }

  /**
   * Call putDeviceType ope with new gateway type
   */
  createNewGatewayType() {
    this.checkNewGatewayTypeValidation();

    if (this.isNewFormValid)
      this.putGatewayType(this.newGatewayType);
  }

  /**
   * Reset new gateway type object
   */
  resetGatewayType() {
    this.newGatewayType = new GatewayType();
  }

  /**
   * Roll back edited gateway type
   */
  resetEditedGatewayType() {
    this.gatewayTypeEdited = false;

    this.editedGatewayType = JSON.parse(JSON.stringify(this.selectedGatewayType));
  }

  /**
   * Check if new gateway type is valid, to enable SAVE button
   */
  checkNewGatewayTypeValidation() {

    const result = this.newGatewayTypeForm.instance.validate();

    this.isNewFormValid = result.isValid && !!this.newGatewayType!.modelName && this.newGatewayType!.modelName.replace(/\s/g, "").length > 0;
  }

  /**
   * Check if edited gateway type is valid, to enable SAVE button
   */
  checkEditGatewayTypeValidation() {
    if (!!this.editGatewayTypeForm) {
      const result = this.editGatewayTypeForm.instance.validate();

      this.isEditFormValid = result.isValid;

      this.gatewayTypeEdited = (JSON.stringify(this.editedGatewayType) !== JSON.stringify(this.selectedGatewayType));

      this.appService.setRegistryEntityIsChanged(this.gatewayTypeEdited && this.isEditMode);

    }
  }

  /**
   * Enable form edit in drawer
   */
  enableEditMode() {
    this.isEditMode = true;
    this.gatewayTypeEdited = false;
    this.updateGatewayTypeTextOptions();
  }

  /**
   * Disable form edit in drawer
   */
  disableEditMode() {
    if (this.gatewayTypeEdited && this.isEditMode) {
      this.showDiscardEditsDialog().show().done((dialogResult: any) => {
        if (dialogResult) {
          this.resetDrawer();
        }
      });
    } else {
      this.resetDrawer();
    }
  }

  saveGatewayType() {
    this.isEditMode = false;
    this.updateGatewayTypeTextOptions();

    this.putGatewayType(this.editedGatewayType!);
  }

  /**
   * Close lateral drawer
   */
  closeDrawer() {
    if (this.gatewayTypeEdited && this.isEditMode) {
      this.showDiscardEditsDialog().show().done((dialogResult: any) => {
        if (dialogResult) {
          this.resetDrawer();
          this.isDrawerOpen = false;
          this.selectedGatewayTypeTab = 0;
          this.resetGatewayTypeGrid();
        }
      });
    } else {
      this.resetDrawer();
      this.isDrawerOpen = false;
      this.selectedGatewayTypeTab = 0;
      this.resetGatewayTypeGrid();
    }
  }

  /**
   * Reset all drawer vars
   */
  resetDrawer() {
    if (!!this.selectedGatewayType) {
      this.editedGatewayType = JSON.parse(JSON.stringify(this.selectedGatewayType));
    }
    this.gatewayTypeEdited = false;
    this.isEditMode = false;
    this.updateGatewayTypeTextOptions();
  }

  resetGatewayTypeGrid() {
    this.selectedGatewayType = undefined;
    this.editedGatewayType = undefined;
    this.selectedRowGatewayType = [];
  }

  /**
   * Show the new gateway type dialog
   */
  showNewGatewayTypeForm() {
    this.newGatewayType = new GatewayType();
    this.addGatewayType = true;
  }

  /**
   * Update form fields when enabling/disabling edit mode
   */
  updateGatewayTypeTextOptions() {

    this.gatewayTypeNewTextOptions = {
      valueChangeEvent: 'keyup',
    };

    this.gatewayTypeNewTextAreaOptions = {
      valueChangeEvent: 'keyup',
      autoResizeEnabled: true
    };

    this.gatewayTypeEditTextOptions = {
      valueChangeEvent: 'keyup',
      disabled: !this.isEditMode
    };

    this.gatewayTypeEditTextAreaOptions = {
      valueChangeEvent: 'keyup',
      disabled: !this.isEditMode,
      autoResizeEnabled: true
    };

    this.gatewayTypeEditCheckBoxOptions = {
      disabled: !this.isEditMode
    }
  }

  /**
   * Ope to delete a gateway type
   */
  deleteGatewayType() {

    // close delete gateway type dialog
    this.deletingGatewayType = false;

    // create cb
    const cb: DelGatewayTypeCB = new DelGatewayTypeCB();
    cb.i.id = this.selectedGatewayType!.id;

    // call ope
    this.gatewayTypeOpeService.delGatewayType(cb).then((_) => {
      this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.GATEWAY_TYPE.TEXT.DELETED"));
      this.getGatewayTypes();
      this.isDrawerOpen = false;
      this.selectedGatewayType = undefined;
    });
  }

}
